import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { checkMaintenanceStatus, MaintenanceStatus } from './prompt-admin-api';
import { useAuth } from './AuthContext';

interface MaintenanceContextType {
    maintenanceMode: MaintenanceStatus;
    isLoading: boolean;
    isBlocked: boolean; // True if user should be blocked (maintenance ON + not admin)
    refresh: () => Promise<void>;
}

const MaintenanceContext = createContext<MaintenanceContextType | undefined>(undefined);

// Check interval for maintenance status (5 minutes)
const CHECK_INTERVAL = 5 * 60 * 1000;

export function MaintenanceProvider({ children }: { children: ReactNode }) {
    const { isAdmin } = useAuth();
    const [maintenanceMode, setMaintenanceMode] = useState<MaintenanceStatus>({
        enabled: false,
        message: '',
        started_at: null
    });
    const [isLoading, setIsLoading] = useState(true);

    // User is blocked if maintenance is ON and they're not admin
    const isBlocked = maintenanceMode.enabled && !isAdmin;

    const fetchMaintenanceStatus = async () => {
        try {
            const status = await checkMaintenanceStatus();
            setMaintenanceMode(status);
        } catch (error) {
            console.error('Failed to check maintenance status:', error);
            // Keep current state on error (fail-open)
        } finally {
            setIsLoading(false);
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchMaintenanceStatus();
    }, []);

    // Periodic check (every 5 minutes)
    useEffect(() => {
        const interval = setInterval(fetchMaintenanceStatus, CHECK_INTERVAL);
        return () => clearInterval(interval);
    }, []);

    // Re-check when user changes (login/logout)
    useEffect(() => {
        if (!isLoading) {
            fetchMaintenanceStatus();
        }
    }, [isAdmin]);

    const value = {
        maintenanceMode,
        isLoading,
        isBlocked,
        refresh: fetchMaintenanceStatus
    };

    return (
        <MaintenanceContext.Provider value={value}>
            {children}
        </MaintenanceContext.Provider>
    );
}

export function useMaintenance() {
    const context = useContext(MaintenanceContext);
    if (context === undefined) {
        throw new Error('useMaintenance must be used within a MaintenanceProvider');
    }
    return context;
}
